// src/components/admin/shared/MediaUploader.js
import React, { useState, useRef } from 'react';
import { Upload, X, Image, Film, CheckCircle, AlertCircle } from 'lucide-react';

const MediaUploader = ({ 
  onUploadComplete, 
  type = 'image', 
  value = null,
  onRemove,
  className = ''
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  
  const fileInputRef = useRef(null);
  
  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };
  
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (type === 'image' && !file.type.startsWith('image/')) {
      setUploadError('Please select an image file');
      return;
    }
    
    if (type === 'video' && !file.type.startsWith('video/')) {
      setUploadError('Please select a video file');
      return;
    }
    
    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      setUploadError('File size should be less than 10MB');
      return;
    }
    
    setIsUploading(true);
    setUploadProgress(0);
    setUploadError(null);
    
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      
      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          const newProgress = prev + Math.random() * 20;
          return newProgress > 90 ? 90 : newProgress;
        });
      }, 300);
      
      const res = await fetch('/api/blog/upload', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formData
      });
      
      clearInterval(progressInterval);
      
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Failed to upload file');
      }
      
      const data = await res.json();
      setUploadProgress(100);
      setUploadSuccess(true);
      
      // Clear the success message after 2 seconds
      setTimeout(() => {
        setUploadSuccess(false);
        setUploadProgress(0);
      }, 2000);
      
      // Call the callback with the file path
      if (onUploadComplete) {
        onUploadComplete(data.filePath);
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      setUploadError(error.message || 'Failed to upload file');
    } finally {
      setIsUploading(false);
      // Reset the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  
  return (
    <div className={`${className}`}>
      {value ? (
        <div className="relative">
          {type === 'image' ? (
            <img 
              src={value}
              alt="Uploaded media"
              className="w-full h-48 object-cover rounded-md"
            />
          ) : (
            <video
              src={value}
              controls
              className="w-full h-48 rounded-md bg-black"
            />
          )}
          
          {onRemove && (
            <button
              type="button"
              onClick={onRemove}
              className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
              title="Remove"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center h-48 bg-gray-100 rounded-md border-2 border-dashed border-gray-300 p-4">
          {isUploading ? (
            <div className="text-center">
              <div className="relative h-2 w-48 bg-gray-200 rounded-full overflow-hidden mb-2">
                <div 
                  className="absolute left-0 top-0 h-full bg-blue-500 transition-all"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
              <p className="text-sm text-gray-600">Uploading... {Math.round(uploadProgress)}%</p>
            </div>
          ) : uploadError ? (
            <div className="text-center text-red-500 mb-2">
              <AlertCircle className="h-8 w-8 mx-auto mb-2" />
              <p className="text-sm">{uploadError}</p>
              <button
                type="button"
                onClick={() => setUploadError(null)}
                className="mt-2 text-sm text-blue-600 hover:text-blue-800"
              >
                Try Again
              </button>
            </div>
          ) : uploadSuccess ? (
            <div className="text-center text-green-500">
              <CheckCircle className="h-8 w-8 mx-auto mb-2" />
              <p className="text-sm">Upload successful!</p>
            </div>
          ) : (
            <>
              {type === 'image' ? (
                <Image className="h-12 w-12 text-gray-400 mb-2" />
              ) : (
                <Film className="h-12 w-12 text-gray-400 mb-2" />
              )}
              <p className="text-sm text-gray-500 mb-2">
                {type === 'image' ? 'No image selected' : 'No video selected'}
              </p>
              <button
                type="button"
                onClick={handleFileSelect}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
              >
                <Upload className="h-4 w-4 mr-2" />
                {type === 'image' ? 'Select Image' : 'Select Video'}
              </button>
              <p className="mt-2 text-xs text-gray-500">
                {type === 'image' 
                  ? 'PNG, JPG, GIF up to 10MB' 
                  : 'MP4, WEBM, AVI up to 10MB'}
              </p>
            </>
          )}
        </div>
      )}
      
      <input
        ref={fileInputRef}
        type="file"
        accept={type === 'image' ? 'image/*' : 'video/*'}
        className="hidden"
        onChange={handleFileChange}
      />
    </div>
  );
};

export default MediaUploader;